import { FC, useState } from 'react'
import { useGetMonitoringPagesQuery } from 'src/reactQuery/useGetMonitoringPagesQuery'
import { useSaveMonitoringPageMutation } from 'src/reactQuery/useSaveMonitoringPageMutation'
import { isNumericPageId } from 'src/utils/isNumericPageId'
import classes from './header.module.scss'

interface HeaderProps {
  activeTab: 'ads' | 'about'
  onTabChange: (tab: 'ads' | 'about') => void
  pageName: string
  savePageId: string
}

export const Header: FC<HeaderProps> = ({
  activeTab,
  onTabChange,
  pageName,
  savePageId,
}) => {
  const [justSaved, setJustSaved] = useState(false)
  const saveMutation = useSaveMonitoringPageMutation({
    onSuccess: () => setJustSaved(true),
  })

  // Already in the monitored list? Then it's saved regardless of this session.
  const { data: monitoringPages } = useGetMonitoringPagesQuery()
  const alreadySaved = !!monitoringPages?.data?.some(
    (page) => page.page_id === savePageId
  )
  const saved = justSaved || alreadySaved

  // savePageId is the advertiser's real numeric id (resolved from the loaded
  // ads). Only numeric ids can be monitored; if the ads haven't resolved a
  // numeric id yet, saving stays disabled.
  const canSave = isNumericPageId(savePageId)

  const handleSave = () => {
    if (saved || saveMutation.isPending || !canSave) return
    saveMutation.mutate({ page_id: savePageId })
  }

  return (
    <div className={classes.headerContainer}>
      <div className={classes.header}>
        <img
          alt={pageName}
          className={classes.logo}
          src={`https://graph.facebook.com/${savePageId}/picture?type=square`}
        />
        <span className={classes.name}>{pageName}</span>
        <button
          className={`${classes.saveBtn} ${saved ? classes.saveBtnSaved : ''}`}
          disabled={!canSave || saved}
          onClick={handleSave}
          title={
            canSave
              ? undefined
              : 'Only pages found by URL, @handle, or ID can be saved'
          }
          type="button"
        >
          {saved
            ? 'Saved'
            : saveMutation.isPending
              ? 'Saving...'
              : 'Save Search'}
          <svg
            fill="none"
            height="16"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill={saved ? '#8080FF' : 'none'}
              stroke="#8080FF"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </button>
      </div>

      <div className={classes.tabs}>
        <button
          className={`${classes.tab} ${activeTab === 'ads' ? classes.tabActive : ''}`}
          onClick={() => onTabChange('ads')}
          type="button"
        >
          Ads
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'about' ? classes.tabActive : ''}`}
          onClick={() => onTabChange('about')}
          type="button"
        >
          About
        </button>
      </div>
    </div>
  )
}
